import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  Delete,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
  Zoom,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  BtnBold,
  BtnBulletList,
  BtnClearFormatting,
  BtnItalic,
  BtnLink,
  BtnNumberedList,
  BtnStrikeThrough,
  BtnStyles,
  BtnUnderline,
  // BtnUndo,
  // DefaultEditor,
  Editor,
  EditorProvider,
  // HtmlButton,
  Separator,
  Toolbar,
  // createButton
} from "react-simple-wysiwyg";
import { myExpariencesService } from "../../services";
import { useSnackbar } from "notistack";
import { useViewport } from "../../helpers";

// import {
//   FormatAlignLeft,
//   FormatAlignCenter,
//   FormatAlignRight,
//   FormatAlignJustify
// } from '@mui/icons-material';
import clsx from "clsx";
import { useCountries } from "use-react-countries";
import {
  getCountryCodeFromFlagPath,
  getTranslatedCode,
  isLanguageReferToAnother,
  isLanguageSupported,
} from "./translationsHelper";

const PREFIX = "TranslationsEditor";

const classes = {
  translatingLoaderContainer: `${PREFIX}-translatingLoaderContainer`,
  loadingSelectFlag: `${PREFIX}-loadingSelectFlag`,
  buttonsContainer: `${PREFIX}-buttonsContainer`,
  buttonContainerVertical: `${PREFIX}-buttonContainerVertical`,
  flagsAutocomplete: `${PREFIX}-flagsAutocomplete`,
  autoTranslate: `${PREFIX}-autoTranslate`,
  autoTranslateToolTip: `${PREFIX}-autoTranslateToolTip`,
  toolTipText: `${PREFIX}-toolTipText`,
  topContainer: `${PREFIX}-topContainer`,
  removeTranslationIcon: `${PREFIX}-removeTranslationIcon`,
  dividerStyle: `${PREFIX}-dividerStyle`,
  marginBottom: `${PREFIX}-marginBottom`,
  marginTop: `${PREFIX}-marginTop`,
  arrowsContainer: `${PREFIX}-arrowsContainer`,
  tradContainer: `${PREFIX}-tradContainer`,
  tradFullWidth: `${PREFIX}-tradFullWidth`,
  realignIcons: `${PREFIX}-realignIcons`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.translatingLoaderContainer}`]: {
    display: "flex",
    alignItems: "center",
  },

  [`& .${classes.loadingSelectFlag}`]: {
    marginRight: "1rem",
  },

  [`& .${classes.buttonsContainer}`]: {
    display: "flex",
    marginBottom: "20px",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      alignItems: "center",
    },
  },

  [`& .${classes.buttonContainerVertical}`]: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      alignItems: "center",
    },
  },

  [`& .${classes.flagsAutocomplete}`]: {
    width: "100%",
    maxWidth: "350px",
    [theme.breakpoints.down("md")]: {
      marginBottom: "1rem",
    },
  },

  [`& .${classes.autoTranslate}`]: {
    margin: "0 1rem",
    height: "55px",
  },

  [`& .${classes.autoTranslateToolTip}`]: {
    cursor: "help",
    [`&:hover .${classes.toolTipText}`]: {
      visibility: "visible",
    },
  },

  [`& .${classes.toolTipText}`]: {
    visibility: "hidden",
    minWidth: "280px",
    backgroundColor: "#D3AF5F",
    color: "rgb(48, 48, 48)",
    textAlign: "center",
    padding: "5px 0",
    borderRadius: "4px",
    position: "absolute",
    top: "-5px",
    left: "35px",
    zIndex: "1",
  },

  [`& .${classes.topContainer}`]: {
    display: "flex",
    justifyContent: "space-between",
    padding: "0 0 0 0.5rem",
  },

  [`& .${classes.removeTranslationIcon}`]: {
    color: theme.palette.primary.main,
    width: "50px",
    height: "50px",
    transform: "translateY(20px)",
  },

  [`& .${classes.dividerStyle}`]: {
    marginTop: "1rem",
  },

  [`& .${classes.marginBottom}`]: {
    marginBottom: "1rem",
  },

  [`& .${classes.marginTop}`]: {
    marginTop: "1rem",
  },

  [`& .${classes.arrowsContainer}`]: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-end",
    marginRight: "1rem",
  },

  [`& .${classes.tradContainer}`]: {
    display: "flex",
  },

  [`& .${classes.tradFullWidth}`]: {
    width: "100%",
  },

  [`& .${classes.realignIcons}`]: {
    display: "flex",
    marginLeft: "-1rem",
  },
}));

const AUTOMATIC_TRANSLATE_LANGUAGES = [
  "FR",
  "GB",
  "ES",
  "GR",
  "IT",
  "JP",
  "CN",
];

const TranslationsEditor = (props) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { width } = useViewport();
  const { countries } = useCountries();
  const [isLoading, setIsLoading] = useState(false);
  const [autoCompleteValue, setAutoCompleteValue] = useState(null);

  const translateValues = (code, oldCode) => {
    setIsLoading(true);
    myExpariencesService
      .translateDescription(props.defaultText, code)
      .then((res) => {
        props.addTranslation(
          {
            lang: oldCode ? oldCode : getTranslatedCode(res.lang),
            text: res.text,
          },
          "translations"
        );
        setIsLoading(false);
      })
      .catch((err) => {
        console.log("error =>", err);
        setIsLoading(false);
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else
          enqueueSnackbar(t("toasts.translateDescriptionError"), {
            variant: "error",
          });
      });
  };

  const handleAddLanguage = (code) => {
    if (props.configurator) {
      const finalCode = getTranslatedCode(code);
      if (
        props.translate &&
        props.defaultText.length > 0 &&
        isLanguageSupported(finalCode)
      )
        translateValues(finalCode, isLanguageReferToAnother(code));
      else {
        if (props.defaultText.length > 0 && !isLanguageSupported(finalCode))
          enqueueSnackbar(t("toasts.translateToLanguageNotSupported"), {
            variant: "error",
          });
        props.addTranslation(
          {
            lang: code,
            text: props.defaultText || "",
          },
          "translations"
        );
      }
    }
  };

  const automaticTranslate = (code) => {
    setIsLoading(true);
    let myLangArray = [];
    code.forEach((element) => {
      let modifiedLangCode = getTranslatedCode(element);
      myLangArray.push(modifiedLangCode);
    });
    myExpariencesService
      .translateMultipleLanguages(props.defaultText, myLangArray)
      .then((res) => {
        const tmp = [];
        res.forEach((element, index) => {
          const langValue = Object.values(element)[0];
          const textValue = Object.values(element)[1];
          tmp.splice(index, 0, {
            lang: getTranslatedCode(langValue),
            text: textValue,
          });
        });
        props.addTranslation(tmp, "translations");
      })
      .catch((err) => {
        console.log("error =>", err);
        setIsLoading(false);
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else
          enqueueSnackbar(t("toasts.translateDescriptionError"), {
            variant: "error",
          });
      })
      .then(() => {
        setIsLoading(false);
      });
  };

  const handleAutomaticTranslation = (code) => {
    if (props.configurator) {
      if (props.translate && props.defaultText.length > 0) {
        automaticTranslate(code);
      }
    }
  };

  const handleRemoveTranslation = (index) => {
    if (props.configurator) props.removeTranslation(index);
  };

  const handleTranslationValue = (value, index) => {
    if (props.configurator) {
      props.handleTranslation(value, "text", index);
    }
  };

  const currentFlag = (code) => {
    return (
      <img
        loading="lazy"
        width="75px"
        src={`https://flagcdn.com/${code.toLowerCase()}.svg`}
        alt=""
      />
    );
  };

  // const BtnAlignLeft = createButton('Align left', <FormatAlignLeft style={{ width: '20px' }} />, 'justifyLeft');
  // const BtnAlignCenter = createButton('Align center', <FormatAlignCenter style={{ width: '20px' }} />, 'justifyCenter');
  // const BtnAlignRight = createButton('Align right', <FormatAlignRight style={{ width: '20px' }} />, 'justifyRight');
  // const BtnAlignJustify = createButton('Align justify', <FormatAlignJustify style={{ width: '20px' }} />, 'justifyFull');

  return (
    <Root>
      <EditorProvider>
        <Editor
          value={props.defaultText}
          onChange={(e) =>
            props.handleDefaultText(e.target.value, props.defaultTextKey)
          }
        >
          <Toolbar>
            {/* <BtnAlignLeft />
            <BtnAlignCenter />
            <BtnAlignRight />
            <BtnAlignJustify /> */}
            <Separator />
            <BtnBold />
            <BtnItalic />
            <BtnUnderline />
            <BtnStrikeThrough />
            <Separator />
            <BtnNumberedList />
            <BtnBulletList />
            <Separator />
            <BtnLink />
            <BtnClearFormatting />
            <Separator />
            <BtnStyles />
          </Toolbar>
        </Editor>
      </EditorProvider>
      <Divider
        light
        className={clsx(classes.marginBottom, classes.marginTop)}
      />
      {!isLoading ? (
        <Box
          className={
            props.displayButtonVerticaly
              ? classes.buttonContainerVertical
              : classes.buttonsContainer
          }
        >
          <Autocomplete
            id="flags-autocomplete"
            className={classes.flagsAutocomplete}
            value={autoCompleteValue}
            options={countries.map((c) => ({
              name: c.name,
              emoji: getCountryCodeFromFlagPath(c.flags.svg || c.flags.png),
              flag: c.flags.svg || c.flags.png,
            }))}
            getOptionLabel={(option) => option.name || ""}
            getOptionDisabled={(option) => !option.emoji}
            loading={isLoading}
            PaperComponent={(props) => {
              return (
                <Paper
                  elavation={12}
                  {...props}
                  sx={{
                    border: "1px rgba(255, 255, 255, 0.6) solid",
                  }}
                />
              );
            }}
            loadingText={"translating..."}
            isOptionEqualToValue={(option, value) => option.name === value.name}
            renderOption={(props, option) => (
              <Box
                component="li"
                sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                {...props}
              >
                <img loading="lazy" width="20" src={option.flag} alt="" />
                {option.name}
              </Box>
            )}
            onChange={(e, value) =>
              value ? handleAddLanguage(value.emoji) : null
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("myExpariences.countrySelection")}
                variant="outlined"
                sx={{ marginBottom: "20px" }}
              />
            )}
          />
          <Box
            sx={{
              height: "100%",
              display: "flex",
              marginTop: props.isCreate ? "1rem" : "0",
            }}
          >
            <Button
              variant="contained"
              disableElevation
              size="medium"
              className={classes.autoTranslate}
              onClick={() =>
                handleAutomaticTranslation(AUTOMATIC_TRANSLATE_LANGUAGES)
              }
            >
              {t("myExpariences.form.translateInMainLanguages")}
            </Button>
            {width >= 768 && (
              <Tooltip
                title={t("myExpariences.form.translateToolTip")}
                placement="right"
                arrow
                sx={{ color: "#D3AF5F", cursor: "help" }}
                TransitionComponent={Zoom}
              >
                <HelpOutlineIcon />
              </Tooltip>
            )}
          </Box>
        </Box>
      ) : (
        <div className={classes.translatingLoaderContainer}>
          <CircularProgress size={35} className={classes.loadingSelectFlag} />
          <Typography variant="body2">{t("loaders.translating")}</Typography>
        </div>
      )}
      {props.elements?.map((item, index) => {
        return (
          <div key={`current-translation-${item.lang || "lang"}-${index + 1}`}>
            <div className={classes.tradContainer}>
              <div className={classes.tradFullWidth}>
                <div className={classes.topContainer}>
                  <div className={classes.realignIcons}>
                    <div className={classes.arrowsContainer}>
                      <IconButton
                        edge="end"
                        aria-label="edit"
                        onClick={() => props.moveTraduction(index, index - 1)}
                        size="large"
                      >
                        <KeyboardArrowUp />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="locate"
                        onClick={() => props.moveTraduction(index, index + 1)}
                        size="large"
                      >
                        <KeyboardArrowDown />
                      </IconButton>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {currentFlag(item.lang)}
                    </div>
                  </div>
                  <IconButton
                    type="button"
                    variant="gold"
                    className={classes.removeTranslationIcon}
                    onClick={() => handleRemoveTranslation(index)}
                    size="small"
                  >
                    <Delete />
                  </IconButton>
                </div>
                <EditorProvider>
                  <Editor
                    value={item.text}
                    onChange={(e) =>
                      handleTranslationValue(e.target.value, index)
                    }
                  >
                    <Toolbar>
                      {/* <BtnAlignLeft />
                  <BtnAlignCenter />
                  <BtnAlignRight />
                  <BtnAlignJustify /> */}
                      <Separator />
                      <BtnBold />
                      <BtnItalic />
                      <BtnUnderline />
                      <BtnStrikeThrough />
                      <Separator />
                      <BtnNumberedList />
                      <BtnBulletList />
                      <Separator />
                      <BtnLink />
                      <BtnClearFormatting />
                      <Separator />
                      <BtnStyles />
                    </Toolbar>
                  </Editor>
                </EditorProvider>
              </div>
            </div>
            <Divider light className={classes.dividerStyle} />
          </div>
        );
      })}
    </Root>
  );
};

export default TranslationsEditor;
